<script generics="T, V extends Variables" lang="ts">
	import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
	import type { TrackProperties } from '@openpanel/web';
	import { createQuery } from '@tanstack/svelte-query';

	import type { Variables, GraphQLClient } from 'graphql-request';
	import { writable, type Writable } from 'svelte/store';
	import { getQuery } from '../../../../query-utils/getQuery';
	import { getQueryKeys } from '../../../../query-utils/getQueryKeys';
	import { createSearchCombobox, type SearchCombobox } from '../../utils/createSearchCombobox';
	import { countSectionResults } from './countSectionResults/countSectionResults';
	import { resolveSearchValue } from './resolveSearchValue/resolveSearchValue';
	import type { SearchClasses, Section } from './Search';
	import { Search } from './Search';

	let {
		emptyValueResponse = undefined,
		ready = true,
		showOverlay = undefined,
		dataCy,
		placeholder = 'Search',
		ariaLabel = undefined,
		combobox = createSearchCombobox(),
		document,
		additionalVariables = writable({}),
		graphQlClient,
		classes = {},
		mixpanelPrefix = undefined,
		showResultsWhenEmpty = false,
		minSearchLength = 0,
		noResultsMessage = undefined,
		viewAllResultsHref = undefined,
		goto = undefined,
		track = undefined,
		onCloseSearch = undefined,
		onEnter = undefined,
		getError = undefined,
		getVariables,
		request = undefined,
		getSections,
		renderTags = undefined,
		renderHeader = undefined,
		...rest
	}: Props = $props();

	interface Props {
		ready?: boolean | undefined;
		showOverlay?: boolean | undefined;
		dataCy: string;

		emptyValueResponse?: T | undefined;
		showResultsWhenEmpty?: boolean;
		// Minimum characters (after trimming) before a search fires. Defaults to
		// 0 so existing consumers keep searching on the first keystroke; the
		// global search sets this to 2 per its spec.
		minSearchLength?: number;
		placeholder?: string;
		ariaLabel?: string;
		combobox: SearchCombobox | undefined;

		// Optional when `request` is given: only the GraphQL path uses them.
		document?: TypedDocumentNode<T, V>;
		additionalVariables?: Writable<object>;
		graphQlClient?: GraphQLClient;
		classes?: SearchClasses | undefined;
		mixpanelPrefix?: string | undefined;
		noResultsMessage?: string | undefined;
		viewAllResultsHref?: string | undefined;
		goto?: ((href: string) => void) | undefined;
		track?: ((event: string, props?: TrackProperties) => void) | undefined;
		onCloseSearch?: (() => void) | undefined;
		onEnter?: ((searchValue: string) => void) | undefined;
		getError?: undefined | ((error?: string) => string | null);
		class?: string;
		getVariables: (value: string, additionalVariables: object) => V | undefined;

		// Replaces the GraphQL request when given, for a consumer reading the
		// same data from REST. Receives the debounced term, not the variables.
		request?: ((value: string, additionalVariables: object) => Promise<T>) | undefined;

		// Receives the (debounced) search term alongside the data so consumers
		// can add sections derived from the term itself (e.g. page links).
		getSections: (data: T | undefined, searchValue?: string) => Section[];

		renderTags?:
			| import('svelte').Snippet<
					[{ section: Section; onCloseSearch: () => void; lastItem: boolean }]
			  >
			| undefined;

		renderHeader?:
			import('svelte').Snippet<[{ searchValue: string; onCloseSearch: () => void }]> | undefined;
	}

	let value = writable('');
	let timer: ReturnType<typeof setTimeout>;

	let open = $derived(combobox.states.open);

	const handleDebouncedKeyUp = (e: {
		target: (EventTarget & { value?: string | undefined }) | null;
	}) => {
		clearTimeout(timer);

		timer = setTimeout(() => {
			// Below minSearchLength the query is not fired, and any prior value is
			// cleared so results hide again when the term is deleted back down.
			const nextValue = resolveSearchValue(e.target?.value, minSearchLength);

			if (nextValue || minSearchLength > 0) {
				value.set(nextValue);
			}
		}, 500);
	};

	// `additionalVariables` is a Writable store wired up by the parent at mount and is not expected to be swapped at runtime; the options thunk tracks its contents reactively.
	const optionsQuery = createQuery(() => {
		if (!$value && !showResultsWhenEmpty) {
			return {
				queryKey: document
					? getQueryKeys(document, getVariables($value, $additionalVariables))
					: [dataCy, 'empty'],
				queryFn: () => {
					return Promise.resolve(emptyValueResponse);
				}
			};
		}

		if (request) {
			const searchValue = $value;
			const variables = $additionalVariables;

			return {
				queryKey: [dataCy, 'rest', searchValue, variables],
				queryFn: () => request(searchValue, variables)
			};
		}

		if (!graphQlClient || !document) {
			throw new Error('QuerySearch needs either `request` or `document` and `graphQlClient`');
		}

		return getQuery(graphQlClient, document, getVariables($value, $additionalVariables));
	});

	let error = $derived(
		getError?.((optionsQuery as { error?: null | { message: string } }).error?.message) || ''
	);

	let sections = $derived(getSections((optionsQuery as { data: T | undefined }).data, $value));

	// The SEARCH event is emitted once the query for a term has settled, rather
	// than when the term changes, so it can carry `result_count` / `zero_results`
	// alongside `search_term`. `lastTrackedSearchTerm` keeps it to one event per
	// term — svelte-query re-emits the same settled state on unrelated store
	// updates, and a term that is retyped after being cleared is a new search.
	let lastTrackedSearchTerm: string | undefined = undefined;

	$effect(() => {
		const searchTerm = $value;

		if (!searchTerm) {
			lastTrackedSearchTerm = undefined;
			return;
		}

		if (!mixpanelPrefix || !track) {
			return;
		}

		// An in-flight or failed query has no result count to report; the event
		// waits for a successful settle rather than reporting a misleading 0.
		const queryState = optionsQuery as { isPending?: boolean; error?: unknown };

		if (queryState?.isPending || queryState?.error) {
			return;
		}

		if (lastTrackedSearchTerm === searchTerm) {
			return;
		}

		lastTrackedSearchTerm = searchTerm;

		const resultCount = countSectionResults(sections);

		track(`${mixpanelPrefix}/SEARCH`, {
			search_term: searchTerm,
			result_count: resultCount,
			zero_results: resultCount === 0
		});
	});
</script>

<Search
	{ready}
	class={rest.class}
	{showOverlay}
	{combobox}
	{dataCy}
	{placeholder}
	{ariaLabel}
	{value}
	{sections}
	{noResultsMessage}
	{minSearchLength}
	{classes}
	{viewAllResultsHref}
	{error}
	{goto}
	{onCloseSearch}
	{onEnter}
	{renderTags}
	{renderHeader}
	loading={(optionsQuery as { isPending: boolean })?.isPending}
	onkeyup={handleDebouncedKeyUp}
	onclick={() => {
		if ($value) {
			open.set(true);
		}
	}}
/>
